import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { AccountSummary } from "@rbim/shared";
import {
  Activity,
  DatabaseBackup,
  MapPinned,
  RefreshCw,
  Settings,
  ShieldCheck,
  Users,
  Wrench,
} from "lucide-react";
import { apiRequest, ApiRequestError } from "../api";
import { useAuth } from "../auth";

type MunicipalityOption = {
  id: string;
  name: string;
  type: "MUNICIPALITY" | "CITY";
  psgcCode: string;
  provinceName: string;
};

type SystemGeographyResponse = {
  municipalities: MunicipalityOption[];
  barangays: Array<{ id: string; municipalityId: string; name: string; psgcCode: string }>;
};

export function SystemAdministrationPage() {
  const { user } = useAuth();
  const systemAdministrator = user?.role === "SYSTEM_ADMINISTRATOR";
  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [geography, setGeography] = useState<SystemGeographyResponse | null>(null);
  const [loading, setLoading] = useState(systemAdministrator);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!systemAdministrator) return;
    let active = true;
    setLoading(true);
    Promise.all([
      apiRequest<{ users: AccountSummary[] }>("/system/users"),
      apiRequest<SystemGeographyResponse>("/system/geography"),
    ])
      .then(([userResponse, geographyResponse]) => {
        if (!active) return;
        setAccounts(userResponse.users);
        setGeography(geographyResponse);
        setMessage("");
      })
      .catch((error) => {
        if (!active) return;
        setMessage(error instanceof ApiRequestError ? error.message : "The administration overview could not be loaded.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [systemAdministrator]);

  const overview = useMemo(() => {
    const municipalityAdmins = accounts.filter((account) => account.role === "MUNICIPAL_CITY_ADMINISTRATOR");
    const technicalAdmins = accounts.filter((account) => account.role === "TECHNICAL_ADMINISTRATOR");
    const activeMunicipalityAdmins = municipalityAdmins.filter((account) => account.status === "ACTIVE");
    const activeTechnicalAdmins = technicalAdmins.filter((account) => account.status === "ACTIVE");
    const coveredMunicipalityIds = new Set(
      activeMunicipalityAdmins.flatMap((account) => account.scope.type === "MUNICIPALITY_CITY" ? [account.scope.municipalityId] : []),
    );
    return {
      municipalityAdmins,
      technicalAdmins,
      activeMunicipalityAdmins,
      activeTechnicalAdmins,
      coveredMunicipalityIds,
    };
  }, [accounts]);

  const coverageRows = useMemo(() => {
    if (!geography) return [];
    return geography.municipalities
      .map((municipality) => {
        const assigned = overview.activeMunicipalityAdmins.filter(
          (account) => account.scope.type === "MUNICIPALITY_CITY" && account.scope.municipalityId === municipality.id,
        ).length;
        return { ...municipality, assigned };
      })
      .sort((left, right) => right.assigned - left.assigned || left.provinceName.localeCompare(right.provinceName) || left.name.localeCompare(right.name))
      .slice(0, 8);
  }, [geography, overview.activeMunicipalityAdmins]);

  return (
    <>
      <header className="page-heading">
        <div>
          <span className="eyebrow">RBIM Platform Administration</span>
          <h1>{systemAdministrator ? "System Dashboard" : "Technical Dashboard"}</h1>
          <p>
            {systemAdministrator
              ? "Manage RBIM platform access and Municipality/City administrator assignments across the approved location catalog."
              : "Monitor synchronization, backups, audit activity, and approved platform settings without routine access to resident records."}
          </p>
        </div>
      </header>

      {message && <div className="form-error" role="alert">{message}</div>}

      {systemAdministrator ? (
        <>
          <div className="form-grid">
            <section className="form-card">
              <div className="section-heading"><h2><MapPinned />Approved locations</h2><p>Municipalities and cities available for administrator assignment.</p></div>
              <dl className="detail-list"><div><dt>Municipality/City</dt><dd>{loading ? "…" : geography?.municipalities.length ?? 0}</dd></div><div><dt>Barangays</dt><dd>{loading ? "…" : geography?.barangays.length ?? 0}</dd></div></dl>
            </section>
            <section className="form-card">
              <div className="section-heading"><h2><Users />Municipality administrators</h2><p>Governance accounts assigned to Municipality/City locations.</p></div>
              <dl className="detail-list"><div><dt>Active</dt><dd>{loading ? "…" : overview.activeMunicipalityAdmins.length}</dd></div><div><dt>Total</dt><dd>{loading ? "…" : overview.municipalityAdmins.length}</dd></div></dl>
            </section>
            <section className="form-card">
              <div className="section-heading"><h2><Wrench />Technical administrators</h2><p>Infrastructure-facing accounts with no routine resident access.</p></div>
              <dl className="detail-list"><div><dt>Active</dt><dd>{loading ? "…" : overview.activeTechnicalAdmins.length}</dd></div><div><dt>Total</dt><dd>{loading ? "…" : overview.technicalAdmins.length}</dd></div></dl>
            </section>
          </div>

          <section className="notice-card">
            <strong>Municipality administration coverage</strong>
            <p>
              {loading
                ? "Loading approved locations and administrator assignments…"
                : `${overview.coveredMunicipalityIds.size} of ${geography?.municipalities.length ?? 0} approved Municipality/City locations currently have at least one active Municipality/City Administrator. Use User Management to assign or reassign locations.`}
            </p>
          </section>

          <section className="table-card">
            <div className="table-title"><MapPinned /><div><h2>Location coverage snapshot</h2><p>Approved locations with active Municipality/City Administrator coverage shown first.</p></div></div>
            <div className="responsive-table">
              <table>
                <thead><tr><th>Province / HUC</th><th>Municipality / City</th><th>Type</th><th>Active administrators</th></tr></thead>
                <tbody>
                  {coverageRows.map((municipality) => <tr key={municipality.id}><td>{municipality.provinceName}</td><td><strong>{municipality.name}</strong><small>PSGC {municipality.psgcCode}</small></td><td>{municipality.type === "CITY" ? "City" : "Municipality"}</td><td><span className={`badge ${municipality.assigned > 0 ? "available" : ""}`}>{municipality.assigned > 0 ? municipality.assigned : "Not assigned"}</span></td></tr>)}
                  {!loading && coverageRows.length === 0 && <tr><td colSpan={4}>No approved Municipality/City locations are available.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : (
        <section className="notice-card">
          <strong>Technical administration only</strong>
          <p>This role is limited to platform monitoring and approved technical settings. Account provisioning and location assignment remain with the System Administrator.</p>
        </section>
      )}

      <section className="table-card" style={{ marginTop: 20 }}>
        <div className="table-title"><ShieldCheck /><div><h2>Administration workspace</h2><p>Open the platform control you need without entering resident-record workflows.</p></div></div>
        <div className="form-grid" style={{ padding: "0 22px 4px" }}>
          {systemAdministrator && <Link className="primary-button" to="/users"><Users />User Management</Link>}
          <Link className="secondary-button" to="/audit"><Activity />Audit History</Link>
          <Link className="secondary-button" to="/synchronization"><RefreshCw />Synchronization</Link>
          <Link className="secondary-button" to="/backup-status"><DatabaseBackup />Backup Status</Link>
          <Link className="secondary-button" to="/settings"><Settings />Settings</Link>
        </div>
      </section>
    </>
  );
}
